import React from 'react';
import { ContextSource, Signal } from '../../types/context.types';
import { SIGNAL_CATEGORY_META } from '../../constants/signalCategories';
import './SignalChip.css';

export function getSourceLabel(
  signal: Signal,
  sourceMap: Map<string, ContextSource>
): string | undefined {
  if (signal.sourceType !== 'upload' || signal.sourceIds.length === 0) return undefined;
  const names = signal.sourceIds
    .map((id) => sourceMap.get(id)?.fileName)
    .filter((n): n is string => !!n);
  return names.length > 0 ? `From ${names.join(', ')}` : undefined;
}

const SOURCE_INDICATORS: Record<string, { icon: string; label: string }> = {
  chat: { icon: '💬', label: 'From chat' },
  upload: { icon: '📄', label: 'From upload' },
  manual: { icon: '✏️', label: 'Manual' },
};

interface SignalChipProps {
  signal: Signal;
  sourceLabel?: string;
  draggable?: boolean;
  onClick?: (signal: Signal) => void;
  onToggle?: (signal: Signal) => void;
  onDelete?: (signal: Signal) => void;
  compact?: boolean;
}

const SignalChip: React.FC<SignalChipProps> = ({
  signal,
  sourceLabel,
  draggable = false,
  onClick,
  onToggle,
  onDelete,
  compact = false,
}) => {
  const meta = SIGNAL_CATEGORY_META[signal.category];
  const color = meta?.color || '#6c757d';
  const categoryLabel = meta?.label || signal.category;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', signal.label);
    e.dataTransfer.setData('application/x-signal', JSON.stringify(signal));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(signal);
    }
  };

  return (
    <span
      className={`signal-chip ${signal.isActive ? 'active' : 'inactive'} ${compact ? 'compact' : ''}`}
      style={{
        '--chip-color': color,
        borderColor: color,
        opacity: signal.isActive ? 1 : 0.5,
      } as React.CSSProperties}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onClick={() => onClick?.(signal)}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick || draggable ? 0 : undefined}
      title={`${categoryLabel}${signal.sourceType ? ` (${sourceLabel || SOURCE_INDICATORS[signal.sourceType]?.label || signal.sourceType})` : ''}${signal.description ? ` — ${signal.description}` : ''}`}
    >
      <span className="signal-chip-dot" style={{ backgroundColor: color }} />
      <span className="signal-chip-label">{signal.label}</span>
      {signal.sourceType && SOURCE_INDICATORS[signal.sourceType] && (
        <span
          className="signal-chip-source"
          title={sourceLabel || SOURCE_INDICATORS[signal.sourceType].label}
          aria-label={sourceLabel || SOURCE_INDICATORS[signal.sourceType].label}
        >
          {SOURCE_INDICATORS[signal.sourceType].icon}
        </span>
      )}
      {onToggle && (
        <button
          className="signal-chip-toggle"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(signal);
          }}
          title={signal.isActive ? 'Disable signal' : 'Enable signal'}
          aria-label={signal.isActive ? 'Disable signal' : 'Enable signal'}
        >
          {signal.isActive ? 'on' : 'off'}
        </button>
      )}
      {onDelete && (
        <button
          className="signal-chip-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(signal);
          }}
          title="Remove signal"
          aria-label="Remove signal"
        >
          &times;
        </button>
      )}
    </span>
  );
};

export default SignalChip;
